import type { SearchDateField, SearchGroup, SearchParams, SearchSort } from '../api';
import { addDays } from '../format';

/**
 * Search state lives entirely in the URL, using the API's own parameter names,
 * so reload, Back and a shared link reproduce the same query, filters, order,
 * grouping and page. `as_of` pins the result set while paging.
 */

export const PAGE_SIZE = 25;
const SORTS: SearchSort[] = ['relevance', 'newest', 'oldest', 'updated', 'source'];
const GROUPS: SearchGroup[] = ['none', 'topic', 'source', 'age'];

export interface SearchUrlState {
  q: string;
  source: string[];
  topic: string[];
  section: string;
  from: string;
  to: string;
  date_field: SearchDateField;
  /** Empty means "server default": relevance with terms, newest without. */
  sort: SearchSort | '';
  group: SearchGroup;
  offset: number;
  as_of: string;
}

export function readSearchState(params: URLSearchParams): SearchUrlState {
  const sort = params.get('sort') ?? '';
  const group = params.get('group') ?? 'none';
  const offset = Number(params.get('offset') ?? '0');
  return {
    q: params.get('q') ?? '',
    source: params.getAll('source').filter(Boolean),
    topic: params.getAll('topic').filter(Boolean),
    section: params.get('section') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    date_field: (['updated', 'event'] as string[]).includes(params.get('date_field') ?? '')
      ? (params.get('date_field') as SearchDateField)
      : 'published',
    sort: (SORTS as string[]).includes(sort) ? (sort as SearchSort) : '',
    group: (GROUPS as string[]).includes(group) ? (group as SearchGroup) : 'none',
    offset: Number.isFinite(offset) && offset > 0 ? Math.floor(offset) : 0,
    as_of: params.get('as_of') ?? '',
  };
}

export function writeSearchState(s: SearchUrlState): URLSearchParams {
  const p = new URLSearchParams();
  if (s.q) p.set('q', s.q);
  for (const v of s.source) p.append('source', v);
  for (const v of s.topic) p.append('topic', v);
  if (s.section) p.set('section', s.section);
  if (s.from) p.set('from', s.from);
  if (s.to) p.set('to', s.to);
  if (s.date_field !== 'published') p.set('date_field', s.date_field);
  if (s.sort) p.set('sort', s.sort);
  if (s.group !== 'none') p.set('group', s.group);
  if (s.offset > 0) p.set('offset', String(s.offset));
  if (s.as_of && s.offset > 0) p.set('as_of', s.as_of);
  return p;
}

/** Any change other than paging starts again at the first page with a fresh result set. */
export function withChange(s: SearchUrlState, change: Partial<SearchUrlState>): SearchUrlState {
  const paging = 'offset' in change || 'as_of' in change;
  return paging ? { ...s, ...change } : { ...s, ...change, offset: 0, as_of: '' };
}

export function toApiParams(s: SearchUrlState): SearchParams {
  return {
    q: s.q || undefined,
    source: s.source.length ? s.source : undefined,
    topic: s.topic.length ? s.topic : undefined,
    section: s.section || undefined,
    from: s.from || undefined,
    to: s.to || undefined,
    date_field: s.date_field !== 'published' ? s.date_field : undefined,
    sort: s.sort || undefined,
    group: s.group !== 'none' ? s.group : undefined,
    limit: PAGE_SIZE,
    offset: s.offset || undefined,
    as_of: s.offset > 0 && s.as_of ? s.as_of : undefined,
  };
}

export type PeriodPreset = 'any' | 'since-yesterday' | '7d' | '30d' | 'custom';

export function presetRange(preset: PeriodPreset, today: string): { from: string; to: string } {
  switch (preset) {
    case 'since-yesterday':
      return { from: addDays(today, -1), to: '' };
    case '7d':
      return { from: addDays(today, -7), to: '' };
    case '30d':
      return { from: addDays(today, -30), to: '' };
    default:
      return { from: '', to: '' };
  }
}

export function detectPreset(s: Pick<SearchUrlState, 'from' | 'to'>, today: string): PeriodPreset {
  if (!s.from && !s.to) return 'any';
  if (!s.to) {
    for (const p of ['since-yesterday', '7d', '30d'] as const) if (presetRange(p, today).from === s.from) return p;
  }
  return 'custom';
}

export const AGE_GROUP_LABELS: Record<string, string> = {
  upcoming: 'Bevorstehend',
  last_24h: 'Letzte 24 Stunden',
  days_1_7: 'Vor 1–7 Tagen',
  days_7_30: 'Vor 7–30 Tagen',
  older: 'Älter als 30 Tage',
  unknown: 'Datum unbekannt',
};
